import React, { useEffect, useState } from "react";
import { Button, Icon, Modal, Table } from "antd";
import { Link } from "react-router-dom";
import moment from "moment";
import { isHtmlContent, getPreviewText } from "../../util/notificationText";

// Maps a notification's metadata.name (set by the backend event/module) to an icon + color.
const NOTIFICATION_ICON_MAP = {
  offline_device: { type: 'disconnect', color: '#f5222d', background: '#fff1f0' },
  refuel: { type: 'thunderbolt', color: '#fa8c16', background: '#fff7e6' },
  'daily-fuel': { type: 'thunderbolt', color: '#fa8c16', background: '#fff7e6' },
  manpower: { type: 'team', color: '#1890ff', background: '#e6f7ff' },
  renew: { type: 'reload', color: '#52c41a', background: '#f6ffed' },
  unserviceable_asset: { type: 'tool', color: '#f5222d', background: '#fff1f0' },
  module: { type: 'setting', color: '#722ed1', background: '#f9f0ff' },
};
const DEFAULT_NOTIFICATION_ICON = { type: 'bell', color: '#13c2c2', background: '#e6fffb' };

const getNotificationIcon = (metadata) => {
  const name = metadata && metadata.name;
  return (name && NOTIFICATION_ICON_MAP[name]) || DEFAULT_NOTIFICATION_ICON;
};

// Renders as an HTML preview when the content contains markup, otherwise as plain text.
const renderMessageContent = (content) => {
  const text = content || 'NA';
  if (isHtmlContent(text)) {
    return <div className="gx-notification-html-content" dangerouslySetInnerHTML={{ __html: text }} />;
  }
  return <div>{text}</div>;
};

const NotificationItem = ({ notification, onRemove, onRead, onView }) => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const { title, message, timestamp, metadata, isRead } = notification;
  const [badge, setBadge] = useState(false);

  useEffect(() => {
    if (timestamp && Number(timestamp) > 0 && moment(timestamp).diff(moment(), 'hours') <= 1) {
      setBadge(true);
    }
  }, [notification]);

  const time = moment(timestamp).format('HH:mm A'); // 17:30 PM
  const preview = getPreviewText(message);
  const notificationIcon = getNotificationIcon(metadata);

  const showModal = () => {
    setIsModalVisible(true)
  }

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  const handleItemClick = () => {
    onRead && onRead(notification)
  }

  const handleRemoveClick = (e) => {
    e.stopPropagation()
    onRemove && onRemove(notification)
  }

  const showModalInfo = () => {
    const { name, ...rest } = metadata || {};

    const devices = rest && rest.devices || []; //devices
    const htmlContent = rest && rest.body || null;
    const modalMessage = message || 'NA';

    switch (name) {
      case 'offline_device':
        const columns = [
          {
            title: 'Asset',
            dataIndex: 'assetCode',
            key: 'assetCode',
            render: (_, record) => {
              return <Link target='_blank' to={`/iot/device-details/${record.assetId}/${record.deviceId}`}><span>{record.assetCode}/{record.ownerCode} <small>({record.altName})</small></span></Link>
            }
          },
          {
            title: 'Device',
            dataIndex: 'thingName',
            key: 'thingName',
            render: (thingName) => {
              return <span>{thingName}</span>
            }
          },
          {
            title: 'Last Seen',
            dataIndex: 'lastConnected',
            key: 'lastConnected',
            render: (lastConnected) => {
              return lastConnected && <span>{new Date(lastConnected).toLocaleString()}</span>
            }
          },
        ];

        return <div className="gx-notification-detail">
          <div className="gx-notification-meta">
            <Table columns={columns} dataSource={devices} />
          </div>
        </div>

      case 'refuel':
      case 'manpower':
      case 'module':
      case 'renew':
        return (
          <div className="gx-notification-detail">
            {htmlContent ? (
              <div className="gx-notification-html-content" dangerouslySetInnerHTML={{ __html: htmlContent }} />
            ) : renderMessageContent(modalMessage)}
          </div>
        );

      default:
        return (<div className="gx-notification-detail">{renderMessageContent(modalMessage)}</div>);
    }
  }

  return (
    <>
      <li className="gx-media" onClick={handleItemClick} style={{ cursor: 'pointer', opacity: isRead ? 0.6 : 1 }}>
        <div className="gx-user-thumb gx-mr-3">
          <span
            className="gx-size-40 gx-notification-type-icon"
            style={{ backgroundColor: notificationIcon.background, color: notificationIcon.color }}
          >
            <Icon type={notificationIcon.type} />
          </span>
          {badge && !isRead ? <span className="gx-badge gx-badge-danger gx-text-white gx-rounded-circle">{'N'}</span> : null}
        </div>
        <div className="gx-media-body">
          <div className="gx-flex-row gx-justify-content-between gx-align-items-center">
            <h5 className="gx-text-capitalize gx-user-name gx-mb-0"><span className="gx-link">{title}</span></h5>
            <span className="gx-meta-date"><small>{time}</small></span>
          </div>
          <p className="gx-fs-sm">{preview}</p>

          <span className="gx-btn gx-btn-sm gx-top2 gx-text-muted" onClick={(e) => {
            e.stopPropagation()
            const name = metadata && metadata.name;
            if (name) {
              showModal()
              onView && onView()
            }
          }} style={{ cursor: 'pointer' }}><i
              className="icon icon-custom-view gx-pr-2" />View</span>

          <span className="gx-btn gx-btn-sm gx-top2 gx-text-muted" onClick={handleRemoveClick}
            style={{ cursor: 'pointer' }}>
            <Icon type="close" className="gx-pr-2" />
            Remove
          </span>
        </div>
      </li>

      <Modal
        title={title}
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={metadata && metadata.url ? [
          <Button key="open-link" type="primary" onClick={() => window.open(metadata.url, '_blank', 'noopener,noreferrer')}>
            Open Link
          </Button>
        ] : null}
        style={{ top: 20, bottom: 20, left: 20, right: 20, height: '250px', width: '50%' }}
      >
        {showModalInfo()}
      </Modal>
    </>
  );
};

export default NotificationItem;
